/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-indexed-object-style */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  effect,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { InfrastructureCatalogService, InfrastructureSelectionService } from '@core/catalogs';
import { LanguageService } from '@core/i18n/language.service';
import { ModulePermissionsService } from '@core/rbac/module-permissions.service';
import { UserTypeId } from '@shared/models/enums';
import { filter, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '@core/services/toast/toast.service';
import { LoadingService } from '@services/loading/loading.service';
import { MenuItem, SectionItem } from './models/sidebar-menu.model';

@Component({
  selector: 'app-sidebar',
  imports: [RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  isSidebarOpen = false;
  activeIcon = 'resources';
  activeSection = 'resources';
  oldActiveIcon = '';
  activeSubSection = '';
  expandedSections: { [key: string]: boolean } = {};

  userId: string;

  private sub: Subscription | undefined;
  private destroy$ = new Subject<void>();
  private hoverTimeout: ReturnType<typeof setTimeout> | null = null;

  translateData: Record<string, string> = {};

  readonly TOTAL_GRID_ITEMS = 5;
  menuItems: MenuItem[] = [];
  sectionItems: Record<string, SectionItem[]> = {};

  readonly perms = inject(ModulePermissionsService);
  private readonly infraCatalog = inject(InfrastructureCatalogService);
  private readonly infraSelection = inject(InfrastructureSelectionService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);

  constructor(
    private langService: LanguageService,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    this.userId = this.auth.getUserId();

    effect(() => {
      const strId = this.infraSelection.selectedStrId();
      if (strId) this.updateCurrentRoute(strId);
    });
  }

  private translates(): void {
    this.langService
      .getSharedDictionary('sidebar')
      .pipe(takeUntil(this.destroy$))
      .subscribe((translation) => {
        this.translateData = translation;

        this.menuItems = [
          {
            id: 'general',
            icon: 'assets/icons/sidebar/general.svg',
            label: this.translateData['general'],
            requiresPermission: false,
            permissionKey: 'General',
            visible: true,
          },
          {
            id: 'resources',
            icon: 'assets/icons/sidebar/resources.svg',
            label: this.translateData['resources'],
            requiresPermission: false,
            permissionKey: 'Resources',
            visible: true,
          },
        ];

        this.sectionItems = {
          general: [
            {
              id: 'general',
              label: this.translateData['manager'] || 'Manager',
              route: '/landing',
            },
          ],
          resources: [
            {
              id: 'resources',
              label: this.translateData['overview'] || 'Overview',
              route: '/resources',
            },
            {
              id: 'organization',
              label: this.translateData['organization'] || 'Organizations',
              route: '/resources/organization',
            },
            {
              id: 'personnel',
              label: this.translateData['personnel'] || 'Personnel',
              route: '/resources/personnel',
              conditionalRoute: {
                condition: 'userType3',
                route: ['/resources/personnel', 'userId'],
              },
            },
            {
              id: 'infrastructure',
              label: this.translateData['infrastructure'] || 'Infrastructures',
              route: '/resources/infrastructure',
              hasSubsections: true,
              subsections: [
                {
                  id: 'networks',
                  label: this.translateData['networks'] || 'Networks',
                  route: '/resources/infrastructure/networks',
                },
              ],
            },
            {
              id: 'aircraft',
              label: this.translateData['aircraft'] || 'Aircraft',
              route: '/resources/aircraft',
              hasSubsections: true,
              subsections: [
                {
                  id: 'fleets',
                  label: this.translateData['fleets'] || 'Fleets',
                  route: '/resources/aircraft/fleets',
                },
              ],
            },
            {
              id: '',
              label: '',
              route: '',
              type: 'separator',
            },
            {
              id: 'petition',
              label: this.translateData['petition'] || 'Petition',
              route: '/resources/petition',
            },
            {
              id: 'issues',
              label: this.translateData['issues'] || 'Issues',
              route: '/resources/issues',
            },
          ],
        };
        this.cdr.markForCheck();
      });
  }

  get visibleMenuItems(): MenuItem[] {
    const visibleItems = this.menuItems.filter((item) => {
      return (
        !item.requiresPermission ||
        (item.requiresPermission && this.perms.canAccessModule(item.permissionKey!))
      );
    });

    const numberOfEmptyItems = Math.max(this.TOTAL_GRID_ITEMS - visibleItems.length, 0);
    const emptyItems = Array.from({ length: numberOfEmptyItems }, () => ({
      id: '',
      icon: '',
      label: '',
      visible: false,
    }));

    return [...visibleItems, ...emptyItems];
  }

  get hasInfrastructures(): boolean {
    return this.infraCatalog.infrastructures().length > 0;
  }

  handleSectionHover(item: any): void {
    if (item.requiresInfrastructure && !this.hasInfrastructures) {
      return;
    }

    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      if (item.requiresPermission) {
        if (this.perms.canAccessModule(item.permissionKey!)) {
          this.activeSectionIcon(item.id);
        }
      } else {
        this.activeSectionIcon(item.id);
      }
    }, 100);
  }

  handleSectionMouseLeave(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  handleSectionClick(item: any, event: Event): void {
    event.preventDefault();

    if (item.requiresInfrastructure && !this.hasInfrastructures) {
      console.log("Error - There's no infrastructure");
      this.toastService.error(
        this.translateData?.['error'] || 'Error',
        this.translateData?.['access_without_infrastructure'] ||
          'Infrastructure must be created to access this feature',
      );
      return;
    }

    if (this.isMobile) {
      this.handleSectionHover(item);
      return;
    }
    if (item.requiresPermission && !this.perms.canAccessModule(item.permissionKey!)) {
      return;
    }
    this.activeSectionIcon(item.id);

    const entryItem = this.resolveSectionEntryItem(item.id);
    if (entryItem) {
      this.handleItemAction(entryItem);
    }
  }

  getIconHref(item: any): string | null {
    if (item.requiresPermission && !this.perms.canAccessModule(item.permissionKey!)) {
      return null;
    }
    const entryItem = this.resolveSectionEntryItem(item.id);
    if (!entryItem) return null;
    const route = this.getRouteLink(entryItem);
    if (!route) return null;
    const commands = Array.isArray(route) ? route : [route];
    return this.router.serializeUrl(this.router.createUrlTree(commands));
  }

  private resolveSectionEntryItem(sectionId: string): SectionItem | undefined {
    return this.getSectionItems(sectionId).find(
      (item: SectionItem) => item.id === sectionId || item.id === 'manager',
    );
  }

  private get isMobile(): boolean {
    return window.innerWidth <= 480;
  }

  handleSubItemClick(event: Event, url: any, mod: any, section: any): void {
    event.stopPropagation();
    event.preventDefault();
    this.reload(url);
  }

  disabledSection(mod: any, section: any): boolean {
    if (this.perms.canAccessSection(mod, section) === false) {
      return true;
    } else {
      return false;
    }
  }

  ngOnInit(): void {
    this.isSidebarOpen = false;
    this.expandedSections = {};
    this.translates();
    this.getDataRoute();

    // Without an access token these two loads can only 401. If the sidebar mounts on a token-less
    // public page (e.g. the header briefly renders during the sign-up deep-link race before
    // `hideHeader` resolves), firing them would trip the auth interceptor into a Keycloak redirect.
    // Skip them here; on authenticated pages the token is already present so nothing changes.
    if (!this.auth.getAccessToken()) {
      return;
    }

    this.loadingService.show();

    const permissionsLoaded = this.perms.loadPermissions().catch((err) => {
      console.error('Error fetching permissions:', err);
    });

    const url = window.location.pathname;
    const skipInfrastructures =
      url === '/resources' ||
      url.includes('/resources/organization') ||
      url.includes('/resources/personnel');
    const infrastructuresLoaded = skipInfrastructures
      ? Promise.resolve()
      : this.infraCatalog
          .loadAll()
          .then((infrastructures) => {
            this.infraSelection.selectDefaultIfMissing(infrastructures);
          })
          .catch((err) => {
            console.error('Error fetching infrastructures:', err);
          })
          .finally(() => {
            this.cdr.markForCheck();
          });

    Promise.allSettled([permissionsLoaded, infrastructuresLoaded]).finally(() =>
      this.loadingService.hide(),
    );
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.sub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    document.body.classList.remove('no-scroll');
  }
  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getDataRoute(): void {
    this.expandedSections = {};
    const deepestChild = this.getDeepestChild(this.route);
    this.activeIcon = deepestChild.snapshot.data['icon'];
    this.activeSection = deepestChild.snapshot.data['section'];

    this.updateExpandedSections(deepestChild);

    if (this.activeIcon === 'document' && this.activeSection === 'document') {
      this.activeSection = deepestChild.snapshot.data['section'];
    } else if (this.activeIcon === 'document') {
      this.activeSection =
        deepestChild.snapshot.params['docMod'] || deepestChild.snapshot.data['section'];
      this.expandedSections = { [this.activeSection]: true };
      this.activeSubSection =
        deepestChild.snapshot.params['docType'] || deepestChild.snapshot.data['subSection'];
    }

    this.oldActiveIcon = this.activeIcon;

    this.sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const child = this.getDeepestChild(this.route);

        this.expandedSections = {};
        this.activeIcon = child.snapshot.data['icon'];
        this.activeSection = child.snapshot.data['section'];

        // Evaluar condición específica para 'document'
        if (this.activeIcon === 'document' && this.activeSection === 'document') {
          this.activeSection = child.snapshot.data['section'];
        } else if (this.activeIcon === 'document') {
          this.activeSection = child.snapshot.params['docMod'] || child.snapshot.data['section'];
          this.expandedSections = { [this.activeSection]: true };
          this.activeSubSection =
            child.snapshot.params['docType'] || child.snapshot.data['subSection'];
        }
        this.updateExpandedSections(child);

        this.oldActiveIcon = this.activeIcon;
      });
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    document.body.classList.toggle('no-scroll', this.isSidebarOpen);
    this.cdr.markForCheck();
  }

  closeSidebar(): void {
    if (!this.isSidebarOpen) return;

    this.isSidebarOpen = false;
    document.body.classList.remove('no-scroll');
    this.expandedSections = { [this.activeSection]: true };
    this.activeIcon = this.oldActiveIcon;
    this.cdr.markForCheck();
  }

  onOverlayClick(): void {
    this.closeSidebar();
  }

  onSidebarClick(event: Event): void {
    event.stopPropagation();
  }

  // Escuchar la tecla Escape para cerrar el sidebar
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.isSidebarOpen) {
      event.preventDefault();
      this.closeSidebar();
    }
  }

  // Opcional: Cerrar sidebar al hacer clic fuera (alternativa al overlay)
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isSidebarOpen) return;
    const target = event.target as HTMLElement;
    const sidebar = target.closest('.sidebar-container');
    const toggleBtn = target.closest('.toggle-btn');

    if (this.isSidebarOpen && !sidebar && !toggleBtn) {
      this.closeSidebar();
    }
  }

  toggleSection(event: Event, item: any): void {
    event.stopPropagation();
    event.preventDefault();

    this.expandedSections = {
      ...this.expandedSections,
      [item.id]: !this.expandedSections[item.id],
    };
    this.cdr.markForCheck();
  }

  activeSectionIcon(icon: string): void {
    this.activeIcon = icon;
    this.cdr.markForCheck();
  }

  reload(url: string): void {
    this.router.navigate([url]);
    this.closeSidebar();
  }

  async handleOrganizationClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.closeSidebar();
    if (this.auth.isSuperAdmin()) {
      this.router.navigate(['/resources/organization']);
    } else {
      const orgId = this.auth.getUser()?.org_id;

      if (orgId) {
        this.router.navigate(['/resources/organization', orgId]);
      }
    }
  }

  private updateCurrentRoute(strId: string): void {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter((s) => s);

    if (urlSegments[0] === 'document' || urlSegments[0] === 'monitor') {
      if (urlSegments.length > 1) {
        urlSegments[1] = strId;
        const newUrl = '/' + urlSegments.join('/');
        if (currentUrl !== newUrl) {
          this.router.navigate([newUrl], { replaceUrl: true });
        }
      }
    }
  }

  private updateExpandedSections(route: ActivatedRoute | null): void {
    if (!route) return;

    const url = this.router.url;
    const urlSegments = url.split('/').filter((s) => s);

    // Fleet
    if (urlSegments.includes('fleets')) {
      this.expandedSections['aircraft'] = true;
      this.activeSection = 'aircraft';
      this.activeSubSection = 'fleets';
    } else if (urlSegments.includes('aircraft')) {
      this.expandedSections['aircraft'] = true;
      this.activeSection = 'aircraft';
      this.activeSubSection = '';
    }

    // Network
    if (urlSegments.includes('networks')) {
      this.expandedSections['infrastructure'] = true;
      this.activeSection = 'infrastructure';
      this.activeSubSection = 'networks';
    } else if (urlSegments.includes('infrastructure')) {
      this.expandedSections['infrastructure'] = true;
      this.activeSection = 'infrastructure';
      this.activeSubSection = '';
    }

    // Document
    if (this.activeIcon === 'document' && this.activeSection !== 'document') {
      this.expandedSections[this.activeSection] = true;
    }
  }

  logout(): void {
    this.auth.logout();
  }

  support(): void {
    this.toastService.warn(
      this.translateData?.['warn'] || 'Warn',
      this.translateData?.['not_implemented_message'] || 'This feature is not yet implemented',
    );
  }

  get isSuperAdmin(): boolean {
    return this.auth.isSuperAdmin();
  }

  getRouteLink(item: any): any[] | string | null {
    if (!item.route) return null;

    if (typeof item.route === 'string') {
      return item.route;
    }

    if (Array.isArray(item.route)) {
      const strId = this.infraSelection.selectedStrId();
      // Verificar si requiere strCode y no está disponible
      if (item.requiresInfrastructure && !strId) {
        console.warn('Cannot navigate: strCode is required but not available');
        return null;
      }

      return item.route.map((segment: string) => {
        if (segment === 'strCode') {
          if (!strId) {
            console.error('strCode is required but not set');
            return 'null'; // O manejar de otra forma
          }
          return strId;
        }
        if (segment === 'userId') return this.userId;
        return segment;
      });
    }

    return null;
  }

  shouldShowItem(item: any): boolean {
    if (item.onlySuperAdmin && !this.isSuperAdmin) {
      return false;
    }

    return true;
  }

  isItemDisabled(item: any): boolean {
    if (item.disabledFor === 'userType3' && this.perms.userType() === UserTypeId.USER) {
      return true;
    }

    if (item.disabledFor === 'notSuperAdmin' && !this.isSuperAdmin) {
      return true;
    }

    if (item.permissionModule && item.permissionSection) {
      return this.disabledSection(item.permissionModule, item.permissionSection);
    }

    return false;
  }

  handleItemAction(item: any, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Verificar si el item está deshabilitado
    if (this.isItemDisabled(item)) {
      console.warn('Item is disabled:', item.id);
      return;
    }

    // Manejar ruta condicional
    if (
      item.conditionalRoute &&
      item.conditionalRoute.condition === 'userType3' &&
      this.perms.userType() === UserTypeId.USER
    ) {
      const route = this.getRouteLink({ route: item.conditionalRoute.route });
      if (route) {
        this.navigateToRoute(route, true);
      }
      return;
    }

    // Navegación normal
    const route = this.getRouteLink(item);
    if (route) {
      this.navigateToRoute(route);
    } else {
      console.error('Cannot generate route for item:', item);
    }
  }

  private navigateToRoute(route: any[] | string, shouldCloseSidebar: boolean = true): void {
    const routeArray = Array.isArray(route) ? route : [route];

    if (shouldCloseSidebar) {
      this.closeSidebar();
    }

    this.router
      .navigate(routeArray)
      .then((success) => {
        if (!success) {
          console.error('Navigation failed for route:', routeArray);
        }
      })
      .catch((error) => {
        console.error('Navigation error:', error);
      });
  }

  getSectionItems(sectionId: string): SectionItem[] {
    return this.sectionItems[sectionId] || [];
  }
}
